import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { AdminProvider } from "@/hooks/useAdminContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Landing from "./pages/Landing";
import PromptPage from "./pages/PromptPage";
import PromptScorePage from "./pages/PromptScorePage";
import PromptFeed from "./pages/PromptFeed";
import Library from "./pages/Library";
import Stats from "./pages/Stats";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import SetNewPassword from "./pages/SetNewPassword";
import ProfileSettings from "./pages/ProfileSettings";
import ExecDashboard from "./pages/ExecDashboard";
import ExecPromptLibrary from "./pages/ExecPromptLibrary";
import Organization from "./pages/Organization";
import Onboarding from "./pages/Onboarding";
import NotFound from "./pages/NotFound";
import { AdminLayout } from "./components/admin/AdminLayout";
import { MainLayout } from "./components/layout/MainLayout";
import AdminHome from "./pages/admin/AdminHome";
import AdminOrgDetail from "./pages/admin/AdminOrgDetail";
import AdminSubmissions from "./pages/admin/AdminSubmissions";
import AdminSystem from "./pages/admin/AdminSystem";
import AdminAIUsage from "./pages/admin/AdminAIUsage";
import AdminLLMConfig from "./pages/admin/AdminLLMConfig";
import TeamManagement from "./pages/TeamManagement";
import SharedPromptPage from "./pages/SharedPromptPage";
import MomentumEngine from "./pages/council/MomentumEngine";
import Recommendations from "./pages/council/Recommendations";
import MaturityJourney from "./pages/council/MaturityJourney";
import AiAPartnership from "./pages/council/AiAPartnership";
import InitiativeRoadmap from "./pages/council/InitiativeRoadmap";
import ToolPortfolio from "./pages/council/ToolPortfolio";
import ToolTrials from "./pages/council/ToolTrials";
import AILandscape from "./pages/council/AILandscape";
import TaskDiscovery from "./pages/council/TaskDiscovery";
import AnalyticsReporting from "./pages/council/AnalyticsReporting";
import AgenticCore from "./pages/council/AgenticCore";
import CosmoHome from "./pages/CosmoHome";
import AIIdeas from "./pages/AIIdeas";
import ContentArticlePage from "./pages/ContentArticlePage";
import ThinkingLayer from "./pages/admin/growth/ThinkingLayer";
import ThinkingLayerIndex from "./pages/admin/growth/ThinkingLayerIndex";
import PlatformPulse from "./pages/admin/growth/PlatformPulse";
import NextBestAction from "./pages/admin/growth/NextBestAction";
import ExperimentEngine from "./pages/admin/growth/ExperimentEngine";
import AiaDigestBuilder from "./pages/admin/growth/AiaDigestBuilder";
import PlaybookBuilder from "./pages/admin/growth/PlaybookBuilder";
import SuccessReportBuilder from "./pages/admin/growth/SuccessReportBuilder";
import ResearchFeed from "./pages/admin/growth/ResearchFeed";
import FeatureGapDetector from "./pages/admin/growth/FeatureGapDetector";
import ProactivePipeline from "./pages/admin/growth/ProactivePipeline";
import { Analytics } from "@vercel/analytics/react";
import { usePageTracking } from "./hooks/usePageTracking";
import WorkflowPage from "./pages/WorkflowPage";
import WorkflowBuilderPage from "./pages/WorkflowBuilderPage";
import BuilderPage from "./pages/BuilderPage";
import OAuthConsent from "./pages/OAuthConsent";
import { FEATURE_COSMO_CHAT, FEATURE_COUNCIL } from "./lib/featureFlags";

const queryClient = new QueryClient();

function PageTracker() {
  usePageTracking();
  return null;
}

function ErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="text-center space-y-4">
        <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
        <p className="text-muted-foreground">An unexpected error occurred. Please try reloading the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

const App = () => (
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
  <ThemeProvider attribute="class" defaultTheme="dark" storageKey="cosmo-theme">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
          <Analytics />
          <BrowserRouter>
            <PageTracker />
            <Routes>
              <Route path="/" element={<Landing />} />
              {FEATURE_COSMO_CHAT && (
                <Route
                  path="/cosmo"
                  element={
                    <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                      <MainLayout>
                        <CosmoHome />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
              )}
              <Route
                path="/workflows"
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <WorkflowPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workflows/new"
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <WorkflowBuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workflows/:id"
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <WorkflowBuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prompt"
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <PromptPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/build/:conversationId"
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <BuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prompt_score/:scoreId"
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <PromptScorePage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/feed" 
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <PromptFeed />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/library" 
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <Library />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/ai-ideas" 
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <AIIdeas />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/ai-ideas/:contentId" 
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <ContentArticlePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/stats" 
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <Stats />
                  </ProtectedRoute>
                } 
              />
              <Route path="/oauth/consent" element={<OAuthConsent />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-new-password" element={<SetNewPassword />} />
              <Route 
                path="/onboarding" 
                element={
                  <ProtectedRoute requireOnboarding={false}>
                    <Onboarding />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <ProfileSettings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/organization" 
                element={
                  <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                    <Organization />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/exec-dashboard" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                    <ExecDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/exec-dashboard/library"
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                    <ExecPromptLibrary />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/team" 
                element={
                  <ProtectedRoute allowedRoles={['client_admin', 'admin']}>
                    <TeamManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/p/:scoreId" 
            element={
              <ProtectedRoute allowedRoles={['user', 'client_admin', 'exec', 'admin']}>
                <SharedPromptPage />
              </ProtectedRoute>
            } 
          />
              {/* Council Routes (feature flagged) */}
              {FEATURE_COUNCIL && (
                <>
                  <Route
                    path="/council/momentum"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <MomentumEngine />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/recommendations"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <Recommendations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/maturity"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <MaturityJourney />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/analytics"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <AnalyticsReporting />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/partnership"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <AiAPartnership />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/initiatives"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <InitiativeRoadmap />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/tools"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <ToolPortfolio />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/trials"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <ToolTrials />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/landscape"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <AILandscape />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/discovery"
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'exec', 'admin']}>
                        <TaskDiscovery />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/council/agentic"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AgenticCore />
                      </ProtectedRoute>
                    }
                  />
                </>
              )}

              {/* Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute allowedRoles={['admin']} requireOnboarding={false}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminHome />} />
                <Route path="org/:orgId" element={<AdminOrgDetail />} />
                <Route path="submissions" element={<AdminSubmissions />} />
                <Route path="ai-usage" element={<AdminAIUsage />} />
                <Route path="llm-config" element={<AdminLLMConfig />} />
                <Route path="system" element={<AdminSystem />} />
                <Route path="growth/proactive" element={<ProactivePipeline />} />
                <Route path="growth/thinking" element={<ThinkingLayerIndex />} />
                <Route path="growth/thinking/:orgId" element={<ThinkingLayer />} />
                <Route path="growth/pulse" element={<PlatformPulse />} />
                <Route path="growth/nextaction" element={<NextBestAction />} />
                <Route path="growth/experiments" element={<ExperimentEngine />} />
                <Route path="growth/digest" element={<AiaDigestBuilder />} />
                <Route path="growth/playbook" element={<PlaybookBuilder />} />
                <Route path="growth/reports" element={<SuccessReportBuilder />} />
                <Route path="growth/research" element={<ResearchFeed />} />
                <Route path="growth/gaps" element={<FeatureGapDetector />} />
              </Route>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          </TooltipProvider>
        </AdminProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </Sentry.ErrorBoundary>
);

export default App;
